import type { Versioned } from "./contract.ts";

/**
 * `contracts/client-api.md` § Request formation, 001 § Requests: `If-Match: "<version>"` on
 * every `PATCH` and `DELETE` of a versioned entity, not only work items, comments and time
 * entries, and on the one `POST` a contract puts behind it (002's incremental link write). The
 * rule is symmetric and every later feature inherits it.
 */
export function ifMatchValue(version: number): string {
  if (!Number.isInteger(version) || version < 0) throw new Error(`If-Match needs an entity version, got ${version}`);
  return `"${version}"`;
}

/** The `If-Match` header for a cached entity. */
export function ifMatchFor(entity: Versioned): { "If-Match": string } {
  return { "If-Match": ifMatchValue(entity.version) };
}

/** The version an `ETag: "<version>"` header carries, or null when the response has none. */
export function versionFromEtag(headers: Headers): number | null {
  const etag = headers.get("ETag");
  if (etag === null) return null;
  const match = /^W\/?"?(\d+)"?$|^"?(\d+)"?$/.exec(etag.trim());
  const digits = match?.[1] ?? match?.[2];
  return digits === undefined ? null : Number(digits);
}

/** The methods a versioned entity's version is required on. */
export function requiresIfMatch(method: string): boolean {
  const upper = method.toUpperCase();
  return upper === "PATCH" || upper === "DELETE";
}
